import type { MouseEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, Tooltip } from "@mui/material";
import type { ColDef, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { ItemRoll } from "../models/RollsReceived";

interface Props {
	rolls: ItemRoll[];
	selectedOrder: number | null;
	onSelect: (roll: ItemRoll) => void;
	onDelete: (roll: ItemRoll) => void;
	writeDisabled: boolean;
	writeDisabledReason: string;
}

interface ActionsCellProps {
	data?: ItemRoll;
	onDelete: (roll: ItemRoll) => void;
	writeDisabled: boolean;
	writeDisabledReason: string;
}

// Inline row action (pinned right): delete a recorded roll. Empty slots (no Id)
// have nothing to remove, so the icon only shows once the slot is filled.
const ActionsCell = ({
	data,
	onDelete,
	writeDisabled,
	writeDisabledReason,
}: ActionsCellProps) => {
	if (!data || data.Id == null) return null;
	const stop = (e: MouseEvent) => e.stopPropagation();
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
			}}
		>
			<Tooltip title={writeDisabled ? writeDisabledReason : "Delete roll"}>
				<span>
					<IconButton
						size="small"
						sx={{ p: 0.25 }}
						disabled={writeDisabled}
						onClick={(e) => {
							stop(e);
							onDelete(data);
						}}
						aria-label="delete roll"
					>
						<DeleteIcon
							fontSize="small"
							color={writeDisabled ? "disabled" : "error"}
						/>
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
};

export const ItemRollsTable = ({
	rolls,
	selectedOrder,
	onSelect,
	onDelete,
	writeDisabled,
	writeDisabledReason,
}: Props) => {
	const gridRef = useRef<AgGridReact<ItemRoll>>(null);

	const columnDefs = useMemo<ColDef<ItemRoll>[]>(
		() => [
			{
				field: "Order",
				headerName: "#",
				width: 55,
				cellStyle: { textAlign: "center" },
				sort: "asc",
			},
			{ field: "Name", headerName: "Foundry Number", flex: 1.2, minWidth: 120 },
			{
				field: "Hardness",
				headerName: "Hardness",
				flex: 0.8,
				minWidth: 85,
				cellStyle: { textAlign: "center" },
			},
			{
				field: "SupplierInvoiceNumber",
				headerName: "Supplier Inv No.",
				flex: 1,
				minWidth: 110,
			},
			{
				colId: "actions",
				headerName: "",
				width: 60,
				sortable: false,
				filter: false,
				resizable: false,
				pinned: "right",
				cellRenderer: ActionsCell,
				cellRendererParams: { onDelete, writeDisabled, writeDisabledReason },
			},
		],
		[onDelete, writeDisabled, writeDisabledReason],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<ItemRoll>) => {
			if (event.data) onSelect(event.data);
		},
		[onSelect],
	);

	const getRowId = useCallback(
		(params: { data: ItemRoll }) => params.data.Order.toString(),
		[],
	);

	return (
		<AgGridReact<ItemRoll>
			ref={gridRef}
			rowData={rolls}
			columnDefs={columnDefs}
			defaultColDef={{ ...globalDefaultColDef, sortable: false, filter: false }}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressContextMenu
			animateRows={false}
			rowHeight={32}
			headerHeight={32}
			getRowStyle={(params) =>
				selectedOrder === params.data?.Order
					? { backgroundColor: "#e3f2fd" }
					: undefined
			}
		/>
	);
};
